/*
 * ATTENTION: This file was automatically created by RADPresentation.HTMLClient.Generator
 */

import { inject, singleton } from "tsyringe";
import type { IRADApplication } from '@/Management/RADApplication';
import RADGeneratedModels from '@/Communication/RADGeneratedModels';
import ExceptionBuilder from '@/Utility/ExceptionBuilder';
import RADEnvironmentProxyInterceptor from '@/Communication/RADEnvironmentProxyInterceptor';
import Utilities from '@/Utility/Utilities';
import * as DTO from "coreframetypings/dto";
import { IRADEnvironmentProxy } from "@/Communication/RADEnvironmentProxy";

@singleton()
export class RADEnvironmentProxyImpl implements IRADEnvironmentProxy {
    private _activeCommunicationCounter: number;
    public clientAction: KnockoutObservable<{ operation: string, args: any }>;
    private waitingActions: JQuery.Deferred<null>[];
    private readonly activeServerCommunicationClass = 'activeServerCommunication';
    private isLoggingOut: boolean = false;

    constructor(
        @inject("RADApplication") private app: IRADApplication,
    ) {
        this._activeCommunicationCounter = 0;
        this.waitingActions = [];
        this.clientAction = ko.observable(null).extend({ notify: 'always' });
        RADEnvironmentProxyInterceptor.intercept(this);
    }

    public whenAllRequestsAreResponded(): JQuery.Promise<null> {
        if (!this.hasActiveServerCommunication())
            return Utilities.resolveImmediately(null);

        const def = $.Deferred();
        this.waitingActions.push(def);

        return def.promise();
    }

    public hasActiveServerCommunication(): boolean {
        return this._activeCommunicationCounter > 0;
    }

    private _args(keys, args) {
        let result = { token: this.app.token };
        for (let i = 0; i < keys.length; i++) {
            result[keys[i]] = args[0][keys[i]];
        }
        return result;
    }

    private _options(args) {
        return $.extend(true, {
            _isWorker: false,
            _convert: true,
            _async: true,
            _fail: (operation, keys, jqXHR: JQuery.jqXHR) => {
                if (jqXHR.status === 500) {
                    ExceptionBuilder.throwDotNet(operation, keys, jqXHR.responseJSON, jqXHR.responseText, args[0]);
                } else if (jqXHR.status === 501 || jqXHR.status === 404 || jqXHR.status === 503) {
                    ExceptionBuilder.onCommunicationError();
                } else if (jqXHR.status === 401) {
                    if (!this.isLoggingOut)
                        this.app.unauthorizedRequestCallback();
                } else if (jqXHR.status !== 200 && jqXHR.status !== 0) {
                    this.app.noConnectionToServerCallback();
                }
            }
        }, args[0]);
    }

    private _convert(params, value) {
        let _converters = {
            DTO: (value) => {
                if (params.type in RADGeneratedModels) {
                    return RADGeneratedModels[params.type](value);
                }
                return value;
            },
            Array: (value) => {
                if (params.type in RADGeneratedModels) {
                    let result = [];
                    for (let i = 0; i < value.length; i++) {
                        result.push(RADGeneratedModels[params.type](value[i]));
                    }
                    return result;
                }
                return value;
            },
            Object: (value) => {
                if (params.type in RADGeneratedModels) {
                    let result = {};
                    for (let key in value) {
                        result[key] = RADGeneratedModels[params.type](value[key]);
                    }
                    return result;
                }
                return value;
            },
            Raw: (value) => {
                return value;
            }
        };
        if (value == null) return null;
        return _converters[params.returns](value);
    }

    private increaseCounter(): void {
        this._activeCommunicationCounter++;

        // Add class to dom
        if (!document.body.classList.contains(this.activeServerCommunicationClass))
            document.body.classList.add(this.activeServerCommunicationClass);
    }

    private decreaseCounter(): void {
        if (--this._activeCommunicationCounter <= 0) {
            // Remove class from dom
            if (document.body.classList.contains(this.activeServerCommunicationClass))
                document.body.classList.remove(this.activeServerCommunicationClass);

            this._activeCommunicationCounter = 0;
            // Resolve all deferreds
            this.waitingActions.forEach(x => x.resolve());
            this.waitingActions = [];
        }
    }

    private _deferred(params) {
        let args = this._args(params.keys, params.arguments);
        let options = this._options(params.arguments);
        let isWorker = options._isWorker === true || params.operation === 'keepAlive' || params.operation === 'updateClientCache' || params.operation === 'getCurrentUserTaskInfo' || params.operation === 'getNotifications';
        if (!isWorker) {
            this.clientAction({ operation: params.operation, args: args });
            this.increaseCounter();
        }
        let deferred: any = $.ajax({
            url: MODEL.HTMLClientBaseURL + 'HTMLClient/Json/' + params.operation,
            type: 'POST',
            headers: { '__RequestVerificationToken': this.app.xcsrfToken },
            cache: false,
            async: options._async,
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(args)
        });
        if (options._convert) {
            deferred = deferred.pipe((value) => {
                return this._convert(params, value);
            });
        }
        if (!isWorker) {
            deferred.fail((jqXHR) => {
                options._fail(params.operation, params.keys, jqXHR);
            });
            deferred.always(() => {
                this.decreaseCounter();
            });
        }
        return deferred;
    }

    public applyDetailViewContext(args: {
        detailViewContextID: string,
        values: Object
    })
        : JQueryDeferred<DTO.IValidationDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'applyDetailViewContext',
            returns: 'DTO',
            type: 'validationDTO',
            keys: ['detailViewContextID', 'values']
        });
    }

    public aquireExclusiveLock(args: {
        detailViewContextID: string
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'aquireExclusiveLock',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['detailViewContextID']
        });
    }

    public aquireLockObject(args: {
        lockGroupID: string,
        pdoID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'aquireLockObject',
            returns: 'Raw',
            type: 'boolean',
            keys: ['lockGroupID', 'pdoID']
        });
    }

    public aquireLockObjects(args: {
        lockGroupID: string,
        pdoIds: Array<number>
    })
        : JQueryDeferred<{ [key: string]: boolean }> {
        return this._deferred({
            arguments: [args],
            operation: 'aquireLockObjects',
            returns: 'Object',
            type: 'boolean',
            keys: ['lockGroupID', 'pdoIds']
        });
    }

    public autoLogout(args: {
        clientInfoID: string
    })
        : JQueryDeferred<boolean> {
        this.isLoggingOut = true;
        return this._deferred({
            arguments: [args],
            operation: 'autoLogout',
            returns: 'Raw',
            type: 'boolean',
            keys: ['clientInfoID']
        });
    }

    public beginTransaction()
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: arguments,
            operation: 'beginTransaction',
            returns: 'Raw',
            type: 'boolean',
            keys: []
        });
    }

    public callOperation(args: {
        pdoID: string,
        operationName: string,
        operationParameters: Array<any>,
        contextParameters: Object
    })
        : JQueryDeferred<DTO.IOperationResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'callOperation',
            returns: 'DTO',
            type: 'operationResultDTO',
            keys: ['pdoID', 'operationName', 'operationParameters', 'contextParameters']
        });
    }

    public callOperationFromDVContext(args: {
        detailViewContextID: string,
        operationName: string,
        operationParameters: Array<any>,
        property_values: Object,
        contextParameters: Object,
        nestedContextName: string,
        withValidation: boolean,
        needsLock?: boolean
    })
        : JQueryDeferred<DTO.IOperationResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'callOperationFromDVContext',
            returns: 'DTO',
            type: 'operationResultDTO',
            keys: ['detailViewContextID', 'operationName', 'operationParameters', 'property_values', 'contextParameters', 'nestedContextName', 'withValidation', 'needsLock']
        });
    }

    public callStaticOperation(args: {
        pdTypeName: string,
        operationName: string,
        operationParameters: Array<any>,
        contextParameters: Object,
        _isWorker?: boolean
    })
        : JQueryDeferred<DTO.IOperationResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'callStaticOperation',
            returns: 'DTO',
            type: 'operationResultDTO',
            keys: ['pdTypeName', 'operationName', 'operationParameters', 'contextParameters']
        });
    }

    public callStaticOperationWithoutTransaction(args: {
        pdTypeName: string,
        operationName: string,
        operationParameters: Array<any>,
        contextParameters: Object
    })
        : JQueryDeferred<DTO.IOperationResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'callStaticOperationWithoutTransaction',
            returns: 'DTO',
            type: 'operationResultDTO',
            keys: ['pdTypeName', 'operationName', 'operationParameters', 'contextParameters']
        });
    }

    public changeLanguage(args: {
        lang: string
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'changeLanguage',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['lang']
        });
    }

    public changePassword(args: {
        oldPassword: string,
        newPassword: string,
        tokenExplicitlyAdded?: boolean
    })
        : JQueryDeferred<DTO.IChangePasswordResponseDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'changePassword',
            returns: 'DTO',
            type: 'changePasswordResponseDTO',
            keys: ['oldPassword', 'newPassword', 'tokenExplicitlyAdded']
        });
    }

    public changePersistenceContext(args: {
        persistenceContextID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'changePersistenceContext',
            returns: 'Raw',
            type: 'boolean',
            keys: ['persistenceContextID']
        });
    }

    public changePrincipal(args: {
        principal: string
    })
        : JQueryDeferred<DTO.IPrincipalDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'changePrincipal',
            returns: 'DTO',
            type: 'principalDTO',
            keys: ['principal']
        });
    }

    public commitTransaction()
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: arguments,
            operation: 'commitTransaction',
            returns: 'Raw',
            type: 'boolean',
            keys: []
        });
    }

    public confirmInlineEditingObjects(args: {
        pdTypeName: string,
        pdoIDs: Array<string>,
        isConfirmed: boolean
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'confirmInlineEditingObjects',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['pdTypeName', 'pdoIDs', 'isConfirmed']
        });
    }

    public connect(args: {
        pdoID: string,
        assoziationName: string,
        topdTypeName: string,
        toPDOID: string,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'connect',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdoID', 'assoziationName', 'topdTypeName', 'toPDOID', 'nestedContextName']
        });
    }

    public connectFromInlineEditing(args: {
        inlineEditingContextId: string,
        targetPdoIds: Array<number>,
        targetPdTypeName: string
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'connectFromInlineEditing',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['inlineEditingContextId', 'targetPdoIds', 'targetPdTypeName']
        });
    }

    public connectMany(args: {
        pdoID: string,
        assoziationName: string,
        topdTypeName: string,
        toPDOIDs: Array<string>,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'connectMany',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdoID', 'assoziationName', 'topdTypeName', 'toPDOIDs', 'nestedContextName']
        });
    }

    public createAndConnectFromUploadedContent(args: {
        contextPdoId: string,
        contextPdTypeName: string,
        targetPdTypeName: string,
        relationName: string,
        name: string,
        isFile: boolean
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createAndConnectFromUploadedContent',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['contextPdoId', 'contextPdTypeName', 'targetPdTypeName', 'relationName', 'name', 'isFile']
        });
    }

    public createAndLockInlineEditingObject(args: {
        pdoID: string,
        contextRelationName: string,
        lockGroupID: string,
        initialValues?: Object
    })
        : JQueryDeferred<DTO.IGenericDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createAndLockInlineEditingObject',
            returns: 'DTO',
            type: 'genericDTO',
            keys: ['pdoID', 'contextRelationName', 'lockGroupID', 'initialValues']
        });
    }

    public createDetailViewContext(args: {
        typeName: string,
        pdoID: string,
        acquireLock: boolean,
        contextTypeName: string,
        contextRelationName: string,
        contextParameters: Object
    })
        : JQueryDeferred<DTO.IDetailViewContextDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createDetailViewContext',
            returns: 'DTO',
            type: 'detailViewContextDTO',
            keys: ['typeName', 'pdoID', 'acquireLock', 'contextTypeName', 'contextRelationName', 'contextParameters']
        });
    }

    public createDetailViewContextAndConnectNew(args: {
        typeName: string,
        contextPDOID: string,
        contextTypeName: string,
        contextRelationName: string,
        contextParameters: Object
    })
        : JQueryDeferred<DTO.IDetailViewContextDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createDetailViewContextAndConnectNew',
            returns: 'DTO',
            type: 'detailViewContextDTO',
            keys: ['typeName', 'contextPDOID', 'contextTypeName', 'contextRelationName', 'contextParameters']
        });
    }

    public createFromInlineEditing(args: {
        inlineEditingContextId: string
    })
        : JQueryDeferred<DTO.ICreatedInlineObjectsDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createFromInlineEditing',
            returns: 'DTO',
            type: 'createdInlineObjectsDTO',
            keys: ['inlineEditingContextId']
        });
    }

    public createInlineEditingContext(args: {
        pdTypeName: string,
        contextPdoId: string,
        contextRelationName: string,
        detailViewContextID: string,
        primarySubAssociation: string,
        nestedContextName: string,
        isSummaryViewMode: boolean,
        removeDefaultProperties: boolean,
        acquireLocks?: boolean
    })
        : JQueryDeferred<DTO.IInlineEditingContextDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createInlineEditingContext',
            returns: 'DTO',
            type: 'inlineEditingContextDTO',
            keys: ['pdTypeName', 'contextPdoId', 'contextRelationName', 'detailViewContextID', 'primarySubAssociation', 'nestedContextName', 'isSummaryViewMode', 'removeDefaultProperties', 'acquireLocks']
        });
    }

    public createIterator(args: {
        pdTypeName: string,
        properties: Array<string>,
        filter: string,
        orderby: string,
        retrieveExtentCount: boolean,
        contextPDO_ID: string
    })
        : JQueryDeferred<DTO.IIteratorDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createIterator',
            returns: 'DTO',
            type: 'iteratorDTO',
            keys: ['pdTypeName', 'properties', 'filter', 'orderby', 'retrieveExtentCount', 'contextPDO_ID']
        });
    }

    public createIteratorForAssoziation(args: {
        pdTypeName: string,
        pdo_id: string,
        assoziationName: string,
        properties: Array<string>,
        filter: string,
        orderby: string,
        retrieveExtentCount: boolean,
        itOperator: DTO.IPDIteratorOperatorDTO
    })
        : JQueryDeferred<DTO.IIteratorDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createIteratorForAssoziation',
            returns: 'DTO',
            type: 'iteratorDTO',
            keys: ['pdTypeName', 'pdo_id', 'assoziationName', 'properties', 'filter', 'orderby', 'retrieveExtentCount', 'itOperator']
        });
    }

    public createIteratorWithOperator(args: {
        pdTypeName: string,
        properties: Array<string>,
        filter: string,
        orderby: string,
        retrieveExtentCount: boolean,
        contextPDO_ID: string,
        itOperator: DTO.IPDIteratorOperatorDTO
    })
        : JQueryDeferred<DTO.IIteratorDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createIteratorWithOperator',
            returns: 'DTO',
            type: 'iteratorDTO',
            keys: ['pdTypeName', 'properties', 'filter', 'orderby', 'retrieveExtentCount', 'contextPDO_ID', 'itOperator']
        });
    }

    public createLockGroup()
        : JQueryDeferred<string> {
        return this._deferred({
            arguments: arguments,
            operation: 'createLockGroup',
            returns: 'Raw',
            type: 'int64',
            keys: []
        });
    }

    public createLogZip(args: {
        days: number
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'createLogZip',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['days']
        });
    }

    public createPersistenceContext()
        : JQueryDeferred<string> {
        return this._deferred({
            arguments: arguments,
            operation: 'createPersistenceContext',
            returns: 'Raw',
            type: 'int64',
            keys: []
        });
    }

    public deleteDetailViewContext(args: {
        detailViewContextID: string
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteDetailViewContext',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['detailViewContextID']
        });
    }

    public deleteDetailViewContextStack(args: {
        detailViewContextIdStack: Array<string>
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteDetailViewContextStack',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['detailViewContextIdStack']
        });
    }

    public deleteIterator(args: {
        iteratorID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteIterator',
            returns: 'Raw',
            type: 'boolean',
            keys: ['iteratorID']
        });
    }

    public deleteIterators(args: {
        iteratorIDs: Array<number>
    })
        : JQueryDeferred<Array<boolean>> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteIterators',
            returns: 'Array',
            type: 'boolean',
            keys: ['iteratorIDs']
        });
    }

    public deleteLockGroup(args: {
        lockGroupID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteLockGroup',
            returns: 'Raw',
            type: 'boolean',
            keys: ['lockGroupID']
        });
    }

    public deleteManyObjects(args: {
        pdTypeName: string,
        pdoIDs: Array<string>
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteManyObjects',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdTypeName', 'pdoIDs']
        });
    }

    public deleteObject(args: {
        pdTypeName: string,
        pdoID: string
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'deleteObject',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdTypeName', 'pdoID']
        });
    }

    public deletePersistenceContext(args: {
        persistenceContextID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'deletePersistenceContext',
            returns: 'Raw',
            type: 'boolean',
            keys: ['persistenceContextID']
        });
    }

    public describeSearchResult(args: {
        pdoId: string
    })
        : JQueryDeferred<{ [key: string]: string }> {
        return this._deferred({
            arguments: [args],
            operation: 'describeSearchResult',
            returns: 'Object',
            type: 'string',
            keys: ['pdoId']
        });
    }

    public disconnect(args: {
        pdoID: string,
        assoziationName: string,
        topdTypeName: string,
        toPDOID: string,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'disconnect',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdoID', 'assoziationName', 'topdTypeName', 'toPDOID', 'nestedContextName']
        });
    }

    public disconnectAndDeleteInlineEditingGridRow(args: {
        pdTypeName: string,
        pdoID: string,
        toPDOID: string,
        topdTypeName: string,
        assoziationName: string
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'disconnectAndDeleteInlineEditingGridRow',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdTypeName', 'pdoID', 'toPDOID', 'topdTypeName', 'assoziationName']
        });
    }

    public disconnectFromInlineEditing(args: {
        inlineEditingContextId: string,
        targetPdoIds: Array<number>,
        targetPdTypeName: string
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'disconnectFromInlineEditing',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['inlineEditingContextId', 'targetPdoIds', 'targetPdTypeName']
        });
    }

    public disconnectMany(args: {
        pdoID: string,
        assoziationName: string,
        topdTypeName: string,
        toPDOIDs: Array<string>,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IChangeObjectDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'disconnectMany',
            returns: 'DTO',
            type: 'changeObjectDTO',
            keys: ['pdoID', 'assoziationName', 'topdTypeName', 'toPDOIDs', 'nestedContextName']
        });
    }

    public exportData(args: {
        iteratorID: string,
        exportType: any,
        columns: Array<string>,
        filter: string,
        showFilter?: boolean
    })
        : JQueryDeferred<string> {
        return this._deferred({
            arguments: [args],
            operation: 'exportData',
            returns: 'Raw',
            type: 'string',
            keys: ['iteratorID', 'exportType', 'columns', 'filter', 'showFilter']
        });
    }

    public flushErrorMessages()
        : JQueryDeferred<{ [key: string]: Array<string> }> {
        return this._deferred({
            arguments: arguments,
            operation: 'flushErrorMessages',
            returns: 'Object',
            type: 'iList`1',
            keys: []
        });
    }

    public getActivityFeed(args: {
        itemAmount: number,
        language: string
    })
        : JQueryDeferred<DTO.IActivityFeedDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getActivityFeed',
            returns: 'DTO',
            type: 'activityFeedDTO',
            keys: ['itemAmount', 'language']
        });
    }

    public getAggregationExtent(args: {
        iteratorID: string,
        aggregation: DTO.IIteratorAggregationDTO
    })
        : JQueryDeferred<any> {
        return this._deferred({
            arguments: [args],
            operation: 'getAggregationExtent',
            returns: 'Raw',
            type: 'object[][]',
            keys: ['iteratorID', 'aggregation']
        });
    }

    public getAllResourceItems(args: {
        lang: string
    })
        : JQueryDeferred<{ [key: string]: string }> {
        return this._deferred({
            arguments: [args],
            operation: 'getAllResourceItems',
            returns: 'Object',
            type: 'string',
            keys: ['lang']
        });
    }

    public getApplication()
        : JQueryDeferred<DTO.IApplicationDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getApplication',
            returns: 'DTO',
            type: 'applicationDTO',
            keys: []
        });
    }

    public getAssignedPrincipals(args: {
        username: string
    })
        : JQueryDeferred<DTO.IAssignedPrincipalsDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getAssignedPrincipals',
            returns: 'DTO',
            type: 'assignedPrincipalsDTO',
            keys: ['username']
        });
    }

    public getAvailableSearchParameters()
        : JQueryDeferred<DTO.ISearchParameterDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getAvailableSearchParameters',
            returns: 'DTO',
            type: 'searchParameterDTO',
            keys: []
        });
    }

    public getCurrentUserTaskInfo()
        : JQueryDeferred<DTO.ITaskInformationDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getCurrentUserTaskInfo',
            returns: 'DTO',
            type: 'taskInformationDTO',
            keys: []
        });
    }

    public getDetailView(args: {
        pdoID: string
    })
        : JQueryDeferred<DTO.IDetailViewDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getDetailView',
            returns: 'DTO',
            type: 'detailViewDTO',
            keys: ['pdoID']
        });
    }

    public getExtent(args: {
        iteratorID: string,
        start: number,
        end: number
    })
        : JQueryDeferred<any> {
        return this._deferred({
            arguments: [args],
            operation: 'getExtent',
            returns: 'Raw',
            type: 'object[][]',
            keys: ['iteratorID', 'start', 'end']
        });
    }

    public getExtentCount(args: {
        iteratorID: string
    })
        : JQueryDeferred<string> {
        return this._deferred({
            arguments: [args],
            operation: 'getExtentCount',
            returns: 'Raw',
            type: 'int64',
            keys: ['iteratorID']
        });
    }

    public getExtentWithPDOIDAsFirstColumn(args: {
        iteratorID: string,
        start: number,
        end: number
    })
        : JQueryDeferred<any> {
        return this._deferred({
            arguments: [args],
            operation: 'getExtentWithPDOIDAsFirstColumn',
            returns: 'Raw',
            type: 'object[][]',
            keys: ['iteratorID', 'start', 'end']
        });
    }

    public getInlineEditingObjects(args: {
        inlineEditingContextId: string,
        iteratorId: string,
        properties: Array<string>,
        start: number,
        end: number
    })
        : JQueryDeferred<DTO.IInlineEditingContextDataDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getInlineEditingObjects',
            returns: 'DTO',
            type: 'inlineEditingContextDataDTO',
            keys: ['inlineEditingContextId', 'iteratorId', 'properties', 'start', 'end']
        });
    }

    public getListView(args: {
        listViewPDO_ID: string
    })
        : JQueryDeferred<DTO.IListViewDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getListView',
            returns: 'DTO',
            type: 'listViewDTO',
            keys: ['listViewPDO_ID']
        });
    }

    public getListViewByName(args: {
        listViewName: string
    })
        : JQueryDeferred<DTO.IListViewDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getListViewByName',
            returns: 'DTO',
            type: 'listViewDTO',
            keys: ['listViewName']
        });
    }

    public getListViewByObject(args: {
        listViewName: string,
        pdoID: string
    })
        : JQueryDeferred<DTO.IListViewDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getListViewByObject',
            returns: 'DTO',
            type: 'listViewDTO',
            keys: ['listViewName', 'pdoID']
        });
    }

    public getListViewForFilter(args: {
        listViewPDO_ID: string
    })
        : JQueryDeferred<DTO.IListViewDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getListViewForFilter',
            returns: 'DTO',
            type: 'listViewDTO',
            keys: ['listViewPDO_ID']
        });
    }

    public getMenuImagesSprite()
        : JQueryDeferred<DTO.IImageSpriteDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getMenuImagesSprite',
            returns: 'DTO',
            type: 'imageSpriteDTO',
            keys: []
        });
    }

    public getMenuTree()
        : JQueryDeferred<DTO.INavigationNodeDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getMenuTree',
            returns: 'DTO',
            type: 'navigationNodeDTO',
            keys: []
        });
    }

    public getNavigationTree()
        : JQueryDeferred<DTO.INavigationNodeDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getNavigationTree',
            returns: 'DTO',
            type: 'navigationNodeDTO',
            keys: []
        });
    }

    public getNotifications()
        : JQueryDeferred<DTO.INotificationsDTO> {
        return this._deferred({
            arguments: arguments,
            operation: 'getNotifications',
            returns: 'DTO',
            type: 'notificationsDTO',
            keys: []
        });
    }

    public getObject(args: {
        pdTypeName: string,
        pdoID: string,
        properties: Array<string>
    })
        : JQueryDeferred<DTO.IGenericDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getObject',
            returns: 'DTO',
            type: 'genericDTO',
            keys: ['pdTypeName', 'pdoID', 'properties']
        });
    }

    public getObjectHistoryInformations(args: {
        pdtypename: string,
        pdoID: string
    })
        : JQueryDeferred<DTO.IObjectHistoryInformationsDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getObjectHistoryInformations',
            returns: 'DTO',
            type: 'objectHistoryInformationsDTO',
            keys: ['pdtypename', 'pdoID']
        });
    }

    public getObjects(args: {
        iteratorID: string,
        start: number,
        end: number
    })
        : JQueryDeferred<Array<DTO.IGenericDTO>> {
        return this._deferred({
            arguments: [args],
            operation: 'getObjects',
            returns: 'Array',
            type: 'genericDTO',
            keys: ['iteratorID', 'start', 'end']
        });
    }

    public getPrincipals()
        : JQueryDeferred<Array<DTO.IPrincipalDTO>> {
        return this._deferred({
            arguments: arguments,
            operation: 'getPrincipals',
            returns: 'Array',
            type: 'principalDTO',
            keys: []
        });
    }

    public getProperties(args: {
        pdoID: string,
        properties: Array<string>
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getProperties',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['pdoID', 'properties']
        });
    }

    public getPropertiesForDetailViewContext(args: {
        detailViewContextID: string,
        pdoID: string,
        properties: Array<string>
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getPropertiesForDetailViewContext',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['detailViewContextID', 'pdoID', 'properties']
        });
    }

    public getPublicUserData(args: {
        key: string
    })
        : JQueryDeferred<string> {
        return this._deferred({
            arguments: [args],
            operation: 'getPublicUserData',
            returns: 'Raw',
            type: 'string',
            keys: ['key']
        });
    }

    public getResourceItem(args: {
        lang: string,
        resourceName: string
    })
        : JQueryDeferred<string> {
        return this._deferred({
            arguments: [args],
            operation: 'getResourceItem',
            returns: 'Raw',
            type: 'string',
            keys: ['lang', 'resourceName']
        });
    }

    public getResourceItems(args: {
        lang: string,
        resourceNames: Array<string>
    })
        : JQueryDeferred<{ [key: string]: string }> {
        return this._deferred({
            arguments: [args],
            operation: 'getResourceItems',
            returns: 'Object',
            type: 'string',
            keys: ['lang', 'resourceNames']
        });
    }

    public getResourceItemsForPDO(args: {
        lang: string,
        pdo_id: string
    })
        : JQueryDeferred<{ [key: string]: string }> {
        return this._deferred({
            arguments: [args],
            operation: 'getResourceItemsForPDO',
            returns: 'Object',
            type: 'string',
            keys: ['lang', 'pdo_id']
        });
    }

    public getResourceItemsForType(args: {
        lang: string,
        pdTypeName: string
    })
        : JQueryDeferred<{ [key: string]: string }> {
        return this._deferred({
            arguments: [args],
            operation: 'getResourceItemsForType',
            returns: 'Object',
            type: 'string',
            keys: ['lang', 'pdTypeName']
        });
    }

    public getResourceItemsForTypes(args: {
        lang: string,
        pdTypeNames: Array<string>
    })
        : JQueryDeferred<{ [key: string]: { [key: string]: string } }> {
        return this._deferred({
            arguments: [args],
            operation: 'getResourceItemsForTypes',
            returns: 'Object',
            type: 'iDictionary`2',
            keys: ['lang', 'pdTypeNames']
        });
    }

    public getSearchExtent(args: {
        start: number,
        end: number
    })
        : JQueryDeferred<DTO.ISearchResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getSearchExtent',
            returns: 'DTO',
            type: 'searchResultDTO',
            keys: ['start', 'end']
        });
    }

    public getSearchSuggestions(args: {
        query: DTO.ISearchQueryDTO
    })
        : JQueryDeferred<DTO.ISearchSuggestionsDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getSearchSuggestions',
            returns: 'DTO',
            type: 'searchSuggestionsDTO',
            keys: ['query']
        });
    }

    public getSummaryView(args: {
        pdtypename: string,
        pdoID: string
    })
        : JQueryDeferred<DTO.ISummaryViewContextDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'getSummaryView',
            returns: 'DTO',
            type: 'summaryViewContextDTO',
            keys: ['pdtypename', 'pdoID']
        });
    }

    public keepAlive(args: {
        clientTime: string
    })
        : JQueryDeferred<DTO.IKeepAliveDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'keepAlive',
            returns: 'DTO',
            type: 'keepAliveDTO',
            keys: ['clientTime']
        });
    }

    public login(args: {
        username: string,
        password: string,
        principal: string
    })
        : JQueryDeferred<DTO.ILoginDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'login',
            returns: 'DTO',
            type: 'loginDTO',
            keys: ['username', 'password', 'principal']
        });
    }

    public loginEncoded(args: {
        username: string,
        password: string,
        principal: string
    })
        : JQueryDeferred<DTO.ILoginDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'loginEncoded',
            returns: 'DTO',
            type: 'loginDTO',
            keys: ['username', 'password', 'principal']
        });
    }

    public loginEncoded2(args: {
        username: string,
        password: string,
        principal: string,
        remainSignedIn: boolean
    })
        : JQueryDeferred<DTO.ILoginDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'loginEncoded2',
            returns: 'DTO',
            type: 'loginDTO',
            keys: ['username', 'password', 'principal', 'remainSignedIn']
        });
    }

    public loginEncodedWithLanguage(args: {
        username: string,
        password: string,
        principal: string,
        language: string,
        remainSignedIn: boolean,
        tokenExplicitlyAdded?: boolean
    })
        : JQueryDeferred<DTO.ILoginDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'loginEncodedWithLanguage',
            returns: 'DTO',
            type: 'loginDTO',
            keys: ['username', 'password', 'principal', 'language', 'remainSignedIn', 'tokenExplicitlyAdded']
        });
    }

    public logout()
        : JQueryDeferred<boolean> {
        this.isLoggingOut = true;
        return this._deferred({
            arguments: arguments,
            operation: 'logout',
            returns: 'Raw',
            type: 'boolean',
            keys: []
        });
    }

    public newObject(args: {
        pdTypeName: string
    })
        : JQueryDeferred<DTO.IGenericDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'newObject',
            returns: 'DTO',
            type: 'genericDTO',
            keys: ['pdTypeName']
        });
    }

    public outputMessage(args: {
        message: string,
        priority: number
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'outputMessage',
            returns: 'Raw',
            type: 'boolean',
            keys: ['message', 'priority']
        });
    }

    public refreshDetailViewContext(args: {
        detailViewContextID: string
    })
        : JQueryDeferred<DTO.IDetailViewContextDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'refreshDetailViewContext',
            returns: 'DTO',
            type: 'detailViewContextDTO',
            keys: ['detailViewContextID']
        });
    }

    public refreshNestedDetailViewContext(args: {
        parentContextID: string,
        nestedContextName: string,
        typeName: string,
        pdoId: string
    })
        : JQueryDeferred<DTO.IDetailViewContextDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'refreshNestedDetailViewContext',
            returns: 'DTO',
            type: 'detailViewContextDTO',
            keys: ['parentContextID', 'nestedContextName', 'typeName', 'pdoId']
        });
    }

    public registerForAutoTranslation(args: {
        pdTypeName: string,
        pdoId: string,
        propertyName: string,
        originLanguage: string
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'registerForAutoTranslation',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['pdTypeName', 'pdoId', 'propertyName', 'originLanguage']
        });
    }

    public releaseLockedInlineEditingObject(args: {
        lockGroupID: string,
        pdoID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'releaseLockedInlineEditingObject',
            returns: 'Raw',
            type: 'boolean',
            keys: ['lockGroupID', 'pdoID']
        });
    }

    public releaseLockedObject(args: {
        lockGroupID: string,
        pdoID: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'releaseLockedObject',
            returns: 'Raw',
            type: 'boolean',
            keys: ['lockGroupID', 'pdoID']
        });
    }

    public releaseLockedObjects(args: {
        lockGroupID: string,
        pdoIds: Array<number>
    })
        : JQueryDeferred<{ [key: string]: boolean }> {
        return this._deferred({
            arguments: [args],
            operation: 'releaseLockedObjects',
            returns: 'Object',
            type: 'boolean',
            keys: ['lockGroupID', 'pdoIds']
        });
    }

    public rollbackTransaction()
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: arguments,
            operation: 'rollbackTransaction',
            returns: 'Raw',
            type: 'boolean',
            keys: []
        });
    }

    public saveObject(args: {
        pdoID: string,
        lockGroupID: string
    })
        : JQueryDeferred<DTO.ISaveResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'saveObject',
            returns: 'DTO',
            type: 'saveResultDTO',
            keys: ['pdoID', 'lockGroupID']
        });
    }

    public saveObjectWithTransferProperties(args: {
        pdoID: string,
        lockGroupID: string,
        values: Object
    })
        : JQueryDeferred<DTO.ISaveResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'saveObjectWithTransferProperties',
            returns: 'DTO',
            type: 'saveResultDTO',
            keys: ['pdoID', 'lockGroupID', 'values']
        });
    }

    public search(args: {
        query: DTO.ISearchQueryDTO
    })
        : JQueryDeferred<DTO.ISearchResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'search',
            returns: 'DTO',
            type: 'searchResultDTO',
            keys: ['query']
        });
    }

    public setProperties(args: {
        pdoID: string,
        values: Object
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'setProperties',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['pdoID', 'values']
        });
    }

    public swap(args: {
        pdoID: string,
        assoziationName: string,
        first: number,
        second: number
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'swap',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['pdoID', 'assoziationName', 'first', 'second']
        });
    }

    public translate(args: {
        originLanguage: string,
        existingTranslations: Object,
        overrideTranslations: boolean
    })
        : JQueryDeferred<DTO.IResultDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'translate',
            returns: 'DTO',
            type: 'resultDTO',
            keys: ['originLanguage', 'existingTranslations', 'overrideTranslations']
        });
    }

    public tryAutoLogin(args: {
        principal: string
    })
        : JQueryDeferred<DTO.ILoginDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'tryAutoLogin',
            returns: 'DTO',
            type: 'loginDTO',
            keys: ['principal']
        });
    }

    public tryToDeleteObject(args: {
        pdTypeName: string,
        pdoIDs: Array<string>
    })
        : JQueryDeferred<DTO.ITryDeletionDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'tryToDeleteObject',
            returns: 'DTO',
            type: 'tryDeletionDTO',
            keys: ['pdTypeName', 'pdoIDs']
        });
    }

    public tryToDisconnectObject(args: {
        pdoID: string,
        assoziationName: string,
        topdTypeName: string,
        pdoIDs: Array<string>
    })
        : JQueryDeferred<DTO.ITryDeletionDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'tryToDisconnectObject',
            returns: 'DTO',
            type: 'tryDeletionDTO',
            keys: ['pdoID', 'assoziationName', 'topdTypeName', 'pdoIDs']
        });
    }

    public updateAndValidateProperties(args: {
        detailViewContextID: string,
        valuesToUpdate: Object,
        valuesToValidate: Array<string>,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IUpdateAndValidateDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'updateAndValidateProperties',
            returns: 'DTO',
            type: 'updateAndValidateDTO',
            keys: ['detailViewContextID', 'valuesToUpdate', 'valuesToValidate', 'nestedContextName']
        });
    }

    public updateAndValidatePropertiesInGenericObject(args: {
        pdoID: string,
        lockGroupID: string,
        valuesToUpdate: Object,
        valuesToValidate: Array<string>,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IUpdateAndValidateDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'updateAndValidatePropertiesInGenericObject',
            returns: 'DTO',
            type: 'updateAndValidateDTO',
            keys: ['pdoID', 'lockGroupID', 'valuesToUpdate', 'valuesToValidate', 'nestedContextName']
        });
    }

    public updateClientCache(args: {
        level: any
    })
        : JQueryDeferred<DTO.IClientUpdateDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'updateClientCache',
            returns: 'DTO',
            type: 'clientUpdateDTO',
            keys: ['level']
        });
    }

    public updateProperties(args: {
        pdoID: string,
        values: Object,
        scopedProperties: Array<string>,
        nestedContextName: string
    })
        : JQueryDeferred<DTO.IUpdateDTO> {
        return this._deferred({
            arguments: [args],
            operation: 'updateProperties',
            returns: 'DTO',
            type: 'updateDTO',
            keys: ['pdoID', 'values', 'scopedProperties', 'nestedContextName']
        });
    }

    public updatePublicUserData(args: {
        key: string,
        value: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'updatePublicUserData',
            returns: 'Raw',
            type: 'boolean',
            keys: ['key', 'value']
        });
    }

    public updateUserSpecificData(args: {
        key: string,
        value: string
    })
        : JQueryDeferred<boolean> {
        return this._deferred({
            arguments: [args],
            operation: 'updateUserSpecificData',
            returns: 'Raw',
            type: 'boolean',
            keys: ['key', 'value']
        });
    }

}